import { JsonSchema } from '@core/models/transformers.model';
import { EntityRef } from '@core/services/gbe-error-parser/warning.model';
import { resolveAllPropertyInformation } from '@core/utils/json-schema-utils';
import { isNil } from '@core/utils/shared-utils';

/** A parsed record paired with the 1-based line number the reader sees for it. */
export interface NumberedRow<T> {
  row: T;
  rowNumber: number;
}

export interface ReferenceCell<T> extends NumberedRow<T> {
  column: string;
}

/**
 * The upload schemas carry no `x-reference`, so the `_uid` / `_uids` suffix of the schema key
 * describing a column is what marks it as holding another entity's identifier.
 */
const UID_KEY = /_uids?$/;

interface SchemaMatch {
  /** The schema key describing the column: its property name, or the pattern it matches. */
  key: string;
  node: JsonSchema;
}

/**
 * The schema node describing a column. A column is often described by a `patternProperties`
 * entry rather than a property of its own — `pickup_site_uid` and its per-operation variants
 * `pickup_site_uid_2`, `_3`… all resolve to the single `^pickup_site_uid` pattern — so the
 * key reported is the pattern's, which is the one that reads as a field name.
 */
function matchColumn(column: string, schema: JsonSchema): SchemaMatch | undefined {
  const properties = schema.properties ?? schema.items?.properties;
  const property = properties?.[column];
  if (!isNil(property)) {
    return { key: column, node: property };
  }

  for (const [pattern, node] of Object.entries(schema.patternProperties ?? {})) {
    const anchored =
      pattern.startsWith('^') || pattern.endsWith('$') ? pattern : `^${pattern}$`;
    try {
      if (new RegExp(anchored).test(column)) {
        return { key: pattern.replace(/^\^/, '').replace(/\$$/, ''), node };
      }
    } catch {
      // An unparseable pattern describes no column; the remaining ones may still match.
    }
  }
  return undefined;
}

/** The entity type a column points at, when the schema names one. */
function declaredEntityType(node: JsonSchema, schema: JsonSchema): string | undefined {
  const resolved = resolveAllPropertyInformation(node, schema).data;
  // A uid-list column declares its target on `items`, not on the array node itself.
  return (resolved?.['x-reference'] ??
    resolved?.['items']?.['x-reference'] ??
    node['x-reference']) as string | undefined;
}

/**
 * The columns of the table holding another entity's uid, mapped to the entity type they
 * point at. `undefined` means the schema declares no type and only the key marks the column
 * as a reference, so any type may match it.
 */
export function referenceColumnsOf(
  columns: string[],
  schema: JsonSchema
): Map<string, string | undefined> {
  const referenceColumns = new Map<string, string | undefined>();

  for (const column of columns) {
    const match = matchColumn(column, schema);
    if (isNil(match)) {
      continue;
    }
    const entityType = declaredEntityType(match.node, schema);
    if (!isNil(entityType) || UID_KEY.test(match.key)) {
      referenceColumns.set(column, entityType);
    }
  }

  return referenceColumns;
}

/**
 * Whether a cell holds the given uid. A uid-list column is a single comma-separated cell, of
 * which the finding names one member.
 */
function cellHolds(value: unknown, uid: string): boolean {
  if (typeof value !== 'string') {
    return value === uid;
  }
  return value === uid || value.split(',').some((member) => member.trim() === uid);
}

/**
 * The cells a backend finding is about: those holding one of the uids it names, in a column
 * that references the kind of entity that uid belongs to.
 *
 * A finding such as `missing_reference` names the uids it could not resolve and nothing
 * else, so matching the value back to a reference column is the only way to say which cell
 * the reader has to fix. Restricting the match to reference columns keeps a coincidental
 * value collision — a name or a code that happens to read like the uid — from being
 * reported as the broken reference.
 */
export function findReferenceCells<T extends Record<string, unknown>>(
  refs: EntityRef[],
  rows: NumberedRow<T>[],
  referenceColumns: Map<string, string | undefined>
): ReferenceCell<T>[] {
  const cells: ReferenceCell<T>[] = [];
  const seen = new Set<string>();

  for (const ref of refs) {
    // A LANDMARK only situates the finding; its cell is context, not the thing to fix.
    if (isNil(ref.uid) || ref.relation === 'LANDMARK') {
      continue;
    }

    for (const { row, rowNumber } of rows) {
      for (const [column, entityType] of referenceColumns) {
        if (!isNil(entityType) && entityType !== ref.entityType) {
          continue;
        }
        if (!cellHolds(row[column], ref.uid)) {
          continue;
        }
        const key = `${rowNumber}:${column}`;
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        cells.push({ row, rowNumber, column });
      }
    }
  }

  return cells;
}
